import React, { useState, useEffect } from 'react';
import { computeOverallDataQuality } from '@/utils/dataQualityUtils';
import LeadDescriptionSnippet from './LeadDescriptionSnippet';
import { useNavigate } from 'react-router-dom';
import { Lead } from '@/types/lead';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, Trash2, Plus, FileText, Search, Phone, Mail, Globe, MoreVertical, StickyNote, Link, Info, BarChart3, FlaskConical, Radar, Send } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import WebSearchPanel from './WebSearchPanel';
import { useToast } from '@/hooks/use-toast';
import { enrichLeadWithWebSearch } from '@/services/webSearchService';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/context/AuthContext';
import { formatEmailForDisplay, getPrimaryEmail } from '@/utils/emailUtils';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { supabase } from '@/integrations/supabase/client';

const getEnrichmentStatus = (status?: string | null, hasData?: boolean) => {
  if (status === 'processing') return { color: 'text-amber-500 animate-pulse', label: 'Processing' };
  if (status === 'completed' || hasData) return { color: 'text-green-500', label: 'Completed' };
  if (status === 'skipped') return { color: 'text-amber-600', label: 'Skipped' };
  if (status === 'failed') return { color: 'text-destructive', label: 'Failed' };
  return { color: 'text-muted-foreground/30', label: 'Not run' };
};

interface CardViewProps {
  leads: Lead[];
  onToggleFavorite: (id: string) => void;
  onDelete: (id: string) => void;
  onAddNote: (leadId: string, content: string) => void;
  onAddDocument: (leadId: string, name: string, url: string, type: string) => void;
}

const CardView: React.FC<CardViewProps> = ({
  leads,
  onToggleFavorite,
  onDelete,
  onAddNote,
  onAddDocument,
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [enriching, setEnriching] = useState<Record<string, boolean>>({});
  const [activeDialog, setActiveDialog] = useState<{ type: 'note' | 'document' | 'webSearch'; lead: Lead } | null>(null);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  
  // Realtime enrichment status overrides
  const [statusOverrides, setStatusOverrides] = useState<Record<string, { ws?: string; dr?: string; cr?: string; fcStage?: string }>>({});

  // Subscribe to realtime enrichment status updates
  useEffect(() => {
    if (leads.length === 0) return;

    const channel = supabase
      .channel('card-view-enrichment-status')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'leads',
      }, (payload) => {
        const updated = payload.new as any;
        const leadId = updated.id;
        // Only track leads we're displaying
        if (!leads.some(l => l.id === leadId)) return;
        
        setStatusOverrides(prev => ({
          ...prev,
          [leadId]: {
            ws: updated.web_search_status ?? prev[leadId]?.ws,
            dr: updated.deep_research_status ?? prev[leadId]?.dr,
            cr: updated.crawl_status ?? prev[leadId]?.cr,
            fcStage: updated.web_search_data?.enrichmentStage ?? undefined,
          }
        }));
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [leads.map(l => l.id).join(',')]);

  const handleEnrichWithWebSearch = async (lead: Lead) => {
    setEnriching(prev => ({ ...prev, [lead.id]: true }));
    try {
      const result = await enrichLeadWithWebSearch(lead);
      
      if (result) {
        toast({
          title: "Web search completed",
          description: `Successfully retrieved web information for ${lead.name}`,
        });
        
        setActiveDialog({ type: 'webSearch', lead });
      } else {
        toast({
          variant: "destructive",
          title: "Web search failed",
          description: "Unable to retrieve web information. Please try again later.",
        });
      }
    } catch (error) {
      console.error('Error enriching lead with web search:', error);
      toast({
        variant: "destructive",
        title: "Web search failed",
        description: "An error occurred while retrieving web information.",
      });
    } finally {
      setEnriching(prev => ({ ...prev, [lead.id]: false }));
    }
  };

  const handleCardClick = (leadId: string) => {
    navigate(`/leads/${leadId}`);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6 animate-fade-in">
      {leads.map((lead) => (
        <Card key={lead.id} elevation="flat" className="border border-border rounded-xl overflow-hidden group transition-all duration-300 hover:-translate-y-1 hover:border-border/80 h-full flex flex-col">
          <div 
            className="cursor-pointer flex-grow flex flex-col"
            onClick={() => handleCardClick(lead.id)}
          >
            <CardHeader className="p-4 sm:p-5 pb-3 bg-white/[0.03] border-b border-border flex-row justify-between items-start space-y-0 flex-shrink-0">
              <div className="overflow-hidden flex-1 min-w-0">
                <h3 className="font-semibold text-xl tracking-tight truncate mb-1">{lead.name}</h3>
                <p className="text-sm text-muted-foreground/80 truncate">{lead.address}</p>
              </div>
              
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(lead.id);
                  }}
                  className={cn(
                    "transition-all hover:scale-110 h-9 w-9 touch-manipulation",
                    lead.isFavorite ? "text-yellow-500" : "text-muted-foreground"
                  )}
                >
                  <Star className={lead.isFavorite ? "h-5 w-5 fill-yellow-500" : "h-5 w-5"} />
                </Button>
                
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" className="h-9 w-9 touch-manipulation">
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={() => setActiveDialog({ type: 'note', lead })}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Note
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveDialog({ type: 'document', lead })}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Document
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setActiveDialog({ type: 'webSearch', lead })}>
                      <Search className="h-4 w-4 mr-2" />
                      Business Details
                    </DropdownMenuItem>
                    {lead.createdBy === user?.id && (
                      <DropdownMenuItem 
                        className="text-destructive focus:text-destructive" 
                        onClick={() => onDelete(lead.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </CardHeader>
            
            <CardContent className="p-4 sm:p-5 pt-4 flex-grow overflow-hidden space-y-4">
              <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-white/[0.03] border border-border">
                {lead.imageUrl && !failedImages.has(lead.id) ? (
                  <>
                    <img
                      src={lead.imageUrl}
                      alt={lead.name}
                      className="w-full h-full object-contain transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                      onError={() => setFailedImages(prev => new Set(prev).add(lead.id))}
                    />
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center text-muted-foreground/50">
                      <FileText className="h-12 w-12 mx-auto mb-2" />
                      <p className="text-xs">No image</p>
                    </div>
                  </div>
                )}
              </div>
              
              <div>
                <TooltipProvider delayDuration={200}>
                  <div className="flex items-center gap-2 mb-3">
                    {(() => {
                      const overrides = statusOverrides[lead.id];
                      const ws = getEnrichmentStatus(overrides?.ws ?? lead.webSearchStatus, !!lead.webSearchData);
                      const fc = overrides?.fcStage === 'firecrawl_search' 
                        ? { color: 'text-amber-500 animate-pulse', label: 'Processing' }
                        : getEnrichmentStatus(
                            (lead.webSearchData as any)?.firecrawlSearchTimestamp ? 'completed' : null,
                            !!(lead.webSearchData as any)?.firecrawlSearchTimestamp
                          );
                      const dr = getEnrichmentStatus(overrides?.dr ?? lead.deepResearchStatus);
                      const cr = getEnrichmentStatus(overrides?.cr ?? lead.crawlStatus);
                      return (
                        <>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Search className={cn("h-3.5 w-3.5", ws.color)} />
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">Web Search: {ws.label}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Radar className={cn("h-3.5 w-3.5", fc.color)} />
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">Firecrawl Search: {fc.label}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <FlaskConical className={cn("h-3.5 w-3.5", dr.color)} />
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">Deep Research: {dr.label}</TooltipContent>
                          </Tooltip>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Globe className={cn("h-3.5 w-3.5", cr.color)} />
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="text-xs">Website Crawl: {cr.label}</TooltipContent>
                          </Tooltip>
                        </>
                      );
                    })()}
                  </div>
                </TooltipProvider>

                <div className="flex flex-wrap gap-2.5 mb-4">
                  {lead.category && (
                    <Badge 
                      variant="outline" 
                      className="text-xs font-medium border-primary/30 bg-primary/10 px-3 py-1 hover:bg-primary/15 transition-colors"
                    >
                      <span className="mr-1.5">📍</span>
                      {lead.category}
                    </Badge>
                  )}
                  
                  {lead.rating && (
                    <Badge 
                      variant="secondary" 
                      className="text-xs font-medium bg-yellow-500/10 border border-yellow-500/20 px-3 py-1"
                    >
                      <Star className="h-3.5 w-3.5 mr-1.5 fill-yellow-500 text-yellow-500" />
                      <span className="font-semibold">{lead.rating}</span>
                      <span className="text-muted-foreground ml-1">({lead.reviewCount})</span>
                    </Badge>
                  )}

                  {lead.webSearchData?.dataQuality?.score != null && (() => {
                    const kc = lead.keyContacts || [];
                    const keyContactsStats = kc.length > 0 ? {
                      total: kc.length,
                      withEmail: kc.filter(c => c.email).length,
                      withPhone: kc.filter(c => c.phone).length,
                    } : undefined;
                    const overall = computeOverallDataQuality(
                      (lead.webSearchData as any)?.dataQualityHistory || [],
                      lead.webSearchData!.dataQuality!,
                      keyContactsStats
                    );
                    const colorClass = overall.score >= 76 ? 'bg-green-500/10 border-green-500/20 text-green-700 dark:text-green-400'
                      : overall.score >= 51 ? 'bg-blue-500/10 border-blue-500/20 text-blue-700 dark:text-blue-400'
                      : overall.score >= 26 ? 'bg-orange-500/10 border-orange-500/20 text-orange-700 dark:text-orange-400'
                      : 'bg-red-500/10 border-red-500/20 text-red-700 dark:text-red-400';
                    return (
                      <Badge variant="outline" className={cn("text-xs font-medium px-3 py-1", colorClass)}>
                        <BarChart3 className="h-3.5 w-3.5 mr-1.5" />
                        <span className="font-semibold">{overall.score}</span>
                        <span className="ml-1 opacity-80">{overall.label}</span>
                      </Badge>
                    );
                  })()}
                </div>

                <LeadDescriptionSnippet
                  summary={lead.webSearchData?.summary}
                  fallback={lead.webSearchData?.linkedIn?.companyDescription}
                  lines={4}
                  className="mb-2"
                />
                
                <div className="space-y-2 text-sm">
                  {lead.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Phone className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <a 
                        href={`tel:${lead.phone}`} 
                        className="hover:text-primary transition-colors truncate hover:underline underline-offset-2 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.phone}
                      </a>
                    </div>
                  )}
                  {lead.email && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Mail className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <a 
                        href={`mailto:${getPrimaryEmail(lead.email)}`} 
                        className="hover:text-primary transition-colors truncate hover:underline underline-offset-2 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {formatEmailForDisplay(lead.email)}
                      </a>
                    </div>
                  )}
                  {lead.website && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Globe className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <a 
                        href={lead.website} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="hover:text-primary transition-colors truncate hover:underline underline-offset-2 flex-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {lead.website?.replace(/^https?:\/\//, '')}
                      </a>
                    </div>
                  )}
                  {lead.lastContactedAt && (
                    <div className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors rounded-md hover:bg-muted/50 p-2 -mx-2">
                      <Send className="h-4 w-4 flex-shrink-0 text-primary/70" />
                      <span className="text-sm" title={format(new Date(lead.lastContactedAt), 'PPpp')}>
                        Last contacted {formatDistanceToNow(new Date(lead.lastContactedAt), { addSuffix: true })}
                      </span>
                    </div>
                  )}
                </div>
                
                {(lead.notes.length > 0 || lead.documents.length > 0) && (
                  <div className="border-t border-border/40 pt-4 mt-4 space-y-4">
                    {lead.notes.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2.5">
                          <StickyNote className="h-3.5 w-3.5 text-primary/70" />
                          Notes ({lead.notes.length})
                        </div>
                        <ul className="text-sm space-y-2">
                          {lead.notes.slice(0, 1).map((note) => (
                            <li key={note.id} className="p-3 bg-white/[0.05] rounded-lg border border-border text-muted-foreground">
                              <p className="line-clamp-2 text-sm">{note.content}</p>
                              <p className="text-xs text-muted-foreground/70 mt-1.5">
                                {format(new Date(note.createdAt), 'MMM d, yyyy')}
                              </p>
                            </li>
                          ))}
                          {lead.notes.length > 1 && (
                            <p className="text-xs text-primary font-medium">
                              +{lead.notes.length - 1} more {lead.notes.length === 2 ? 'note' : 'notes'}
                            </p>
                          )}
                        </ul>
                      </div>
                    )}
                    
                    {lead.documents.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2.5">
                          <FileText className="h-3.5 w-3.5 text-primary/70" />
                          Documents ({lead.documents.length})
                        </div>
                        <ul className="text-sm space-y-1.5">
                          {lead.documents.slice(0, 2).map((doc) => (
                            <li key={doc.id}>
                              <a 
                                href={doc.url} 
                                className="flex items-center gap-2 text-primary hover:text-primary/80 text-xs truncate p-2 rounded-md hover:bg-primary/5 border border-transparent hover:border-primary/20 transition-all group/doc" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <FileText className="h-3.5 w-3.5 flex-shrink-0 group-hover/doc:scale-110 transition-transform" />
                                <span className="truncate">{doc.name}</span>
                              </a>
                            </li>
                          ))}
                          {lead.documents.length > 2 && (
                            <p className="text-xs text-primary font-medium pl-2 pt-1">
                              +{lead.documents.length - 2} more
                            </p>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </div>

        </Card>
      ))}

      {/* Shared dialogs - rendered once outside the loop */}
      <Dialog
        open={activeDialog?.type === 'note'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      >
        <DialogContent className="sm:max-w-[480px] p-0 elevation-floating backdrop-blur-md">
          <DialogHeader className="p-6 pb-4 bg-white/[0.03] border-b border-border">
            <div className="flex items-center gap-3">
              <StickyNote className="h-5 w-5 text-primary" />
              <DialogTitle className="text-2xl font-semibold tracking-tight">Add Note</DialogTitle>
            </div>
            <DialogDescription className="text-muted-foreground/70">Keep important information about this lead</DialogDescription>
          </DialogHeader>
          {activeDialog?.type === 'note' && (
            <AddNoteForm
              leadId={activeDialog.lead.id}
              onAddNote={(leadId, content) => {
                onAddNote(leadId, content);
                setActiveDialog(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={activeDialog?.type === 'document'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      >
        <DialogContent className="sm:max-w-[480px] p-0 elevation-floating backdrop-blur-md">
          <DialogHeader className="p-6 pb-4 bg-white/[0.03] border-b border-border">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-primary" />
              <DialogTitle className="text-2xl font-semibold tracking-tight">Add Document</DialogTitle>
            </div>
            <DialogDescription className="text-muted-foreground/70">Attach important files or links to this lead</DialogDescription>
          </DialogHeader>
          {activeDialog?.type === 'document' && (
            <AddDocumentForm
              leadId={activeDialog.lead.id}
              onAddDocument={(leadId, name, url, type) => {
                onAddDocument(leadId, name, url, type);
                setActiveDialog(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={activeDialog?.type === 'webSearch'}
        onOpenChange={(open) => !open && setActiveDialog(null)}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Web Information for {activeDialog?.lead.name}</DialogTitle>
            <DialogDescription>
              Detailed information gathered from across the web
            </DialogDescription>
          </DialogHeader>
          {activeDialog?.type === 'webSearch' && (
            <WebSearchPanel
              lead={activeDialog.lead}
              onDataUpdated={() => {}}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface AddNoteFormProps {
  leadId: string;
  onAddNote: (leadId: string, content: string) => void;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ leadId, onAddNote }) => {
  const [note, setNote] = React.useState('');
  const maxLength = 500;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (note.trim()) {
      onAddNote(leadId, note);
      setNote('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      <div className="p-6 pt-5 space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground/90">Note Content</label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="What would you like to remember about this lead?"
            className="min-h-[140px] p-4 transition-all duration-200"
            maxLength={maxLength}
          />
          <div className="flex justify-between items-center">
            <p className="text-xs text-muted-foreground">⌘+Enter to save</p>
            <span className="text-xs text-muted-foreground">{note.length}/{maxLength}</span>
          </div>
        </div>
      </div>
      <div className="border-t border-border/40 px-6 py-4 bg-muted/20 flex justify-end gap-3">
        <Button type="submit" variant="gradient" disabled={!note.trim()}>
          Save Note
        </Button>
      </div>
    </form>
  );
};

interface AddDocumentFormProps {
  leadId: string;
  onAddDocument: (leadId: string, name: string, url: string, type: string) => void;
}

const AddDocumentForm: React.FC<AddDocumentFormProps> = ({ leadId, onAddDocument }) => {
  const [name, setName] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [urlError, setUrlError] = React.useState('');

  const validateUrl = (urlString: string) => {
    if (!urlString) {
      setUrlError('');
      return false;
    }
    try {
      new URL(urlString);
      setUrlError('');
      return true;
    } catch {
      setUrlError('Please enter a valid URL');
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim() && url.trim() && validateUrl(url)) {
      // Simple file type inference
      const fileExtension = url.split('.').pop()?.toLowerCase() || '';
      const type = fileExtension === 'pdf' ? 'pdf' : 
                  ['jpg', 'jpeg', 'png', 'gif'].includes(fileExtension) ? 'image' : 
                  'document';
      
      onAddDocument(leadId, name, url, type);
      setName('');
      setUrl('');
      setUrlError('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && name.trim() && url.trim()) {
      handleSubmit(e);
    }
  };

  const isValid = name.trim() && url.trim() && !urlError;

  return (
    <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
      <div className="p-6 pt-5 space-y-6">
        <div className="space-y-2">
          <label htmlFor="docName" className="flex items-center gap-2 text-sm font-medium text-foreground/90">
            <FileText className="h-4 w-4" />
            Document Name <span className="text-destructive">*</span>
          </label>
          <Input
            id="docName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Contract, Proposal, Quote"
            className="transition-all duration-200"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="docUrl" className="flex items-center gap-2 text-sm font-medium text-foreground/90">
            <Link className="h-4 w-4" />
            Document URL <span className="text-destructive">*</span>
          </label>
          <Input
            id="docUrl"
            type="url"
            value={url}
            onChange={(e) => {
              setUrl(e.target.value);
              validateUrl(e.target.value);
            }}
            placeholder="https://example.com/document.pdf"
            className={`transition-all duration-200 ${urlError ? 'border-destructive/50 focus-visible:ring-destructive/20' : ''}`}
            hasError={!!urlError}
          />
          {urlError ? (
            <p className="text-xs text-destructive font-medium flex items-center gap-1">
              {urlError}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              Link to PDF, Google Doc, or any online file
            </p>
          )}
        </div>
      </div>
      <div className="border-t border-border/40 px-6 py-4 bg-muted/20 flex justify-between items-center">
        <span className="text-xs text-muted-foreground">⌘+Enter to save</span>
        <Button type="submit" variant="gradient" disabled={!isValid}>
          Add Document
        </Button>
      </div>
    </form>
  );
};

export default CardView;
